/* ============================================================
   EcoLunch · 21 Unique flow diagrams (data-driven schemas)
   3-layer vertical layout: Payer → Hub → Beneficiary
   Each schema declares its own nodes, edges, colors and fees.
   ============================================================ */

/* ============================================================
   FR → EN translation map for in-diagram strings
   (node labels, fees, notes, edge labels)
   Missing keys pass through unchanged.
   ============================================================ */
export const FR_EN_MAP = {
  /* ---- Node labels ---- */
  "Parent": "Parent",
  "Parent principal": "Primary parent",
  "Coparent / tier": "Co-parent / 3rd party",
  "Parent / Sponsor": "Parent / Sponsor",
  "Gestionnaire": "Manager",
  "Garderie / CPE": "Daycare / CPE",
  "Garderie": "Daycare",
  "Garderie · facture": "Daycare · invoice",
  "Garderie · calendrier": "Daycare · schedule",
  "Moteur de facturation": "Billing engine",
  "Présences validées": "Attendance validated",
  "Camp": "Camp",
  "Camp · inscription": "Camp · registration",
  "Camp · repas inclus": "Camp · meals included",
  "Camp · payable": "Camp · payable",
  "Camp / Club · activité": "Camp / Club · activity",
  "Camp · option ajoutée": "Camp · add-on",
  "Inscription confirmée": "Registration confirmed",
  "Club · adhésion": "Club · membership",
  "Club · payable": "Club · payable",
  "École": "School",
  "École · contexte": "School · context",
  "École / CSS · facturier": "School / board · biller",
  "École · payeur": "School · payer",
  "École · échéancier": "School · schedule",
  "Traiteur · payable": "Caterer · payable",
  "Fournisseur · payable": "Provider · payable",
  "Titulaire": "Holder",
  "Titulaire · solde": "Holder · balance",
  "Source de fonds": "Funding source",
  "Carte virtuelle": "Virtual card",
  "EcoLunch Hub": "EcoLunch Hub",
  "EcoWallet": "EcoWallet",
  "EcoWallet · source": "EcoWallet · source",
  "Apple/Google Pay": "Apple/Google Pay",
  "Réseau cartes": "Card network",
  "Autorisation": "Authorization",
  "Commerçant externe": "External merchant",
  "Achat interne": "Internal purchase",
  "Bénéficiaire interne": "Internal beneficiary",
  "Wallet familial": "Family wallet",
  "Wallet institutionnel": "Institutional wallet",
  "Wallet du payeur": "Payer's wallet",
  "Ledger": "Ledger",
  "Ledger · groupe": "Ledger · group",
  "Ledger · 4 IDs": "Ledger · 4 IDs",
  "Ledger · per-day": "Ledger · per-day",
  "Ledger · per-child": "Ledger · per-child",
  "Ledger · subvention": "Ledger · subsidy",
  "Ledger · échéances": "Ledger · installments",
  "Ledger · périodique": "Ledger · periodic",
  "Ledger · ajusté": "Ledger · adjusted",
  "Ledger · split": "Ledger · split",
  "Ledger · échéancier": "Ledger · schedule",
  "Ledger · cycle": "Ledger · cycle",
  "Ledger · add-on": "Ledger · add-on",
  "Ledger · split rule": "Ledger · split rule",
  "Ledger · external": "Ledger · external",
  "Ledger · chain ID": "Ledger · chain ID",
  "Carte · Apple/Google · Interac · Sezzle · PAD": "Card · Apple/Google · Interac · Sezzle · PAD",
  "PAD · Carte · Interac récurrent": "PAD · Card · recurring Interac",
  "Auto-load récurrent": "Recurring auto-load",
  "Pay-in (carte/PAD)": "Pay-in (card/PAD)",
  "Pay-in carte": "Pay-in card",
  "Pay-in consolidé": "Consolidated pay-in",
  "Pay-in (PAD/carte)": "Pay-in (PAD/card)",
  "Chargement institutionnel": "Institutional load-up",
  "Pay-in école (B2B)": "School pay-in (B2B)",
  "Pay-in école": "School pay-in",
  "Pay-in unique": "Single pay-in",
  "Pay-in 50%": "Pay-in 50%",
  "Pay-in": "Pay-in",
  "Auto-load programmé": "Scheduled auto-load",
  "Auto-loads programmés": "Scheduled auto-loads",
  "Auto-load périodique": "Periodic auto-load",
  "PAD récurrent": "Recurring PAD",
  "Dépôt initial": "Initial deposit",

  /* ---- Fee labels (feeIn / feeOut) ---- */
  "2.9% + $0.30": "2.9% + $0.30",
  "2.0% + $0.25": "2.0% + $0.25",
  "Tarif B2B": "B2B rate",
  "Tarif B2B école": "B2B school rate",
  "Tarif réduit PAD": "Reduced PAD rate",
  "Tarif réduit": "Reduced rate",
  "Frais réduit récurrence": "Reduced recurring fee",
  "Variable": "Variable",
  "Frais réseau": "Network fee",
  "Réseau credit": "Network credit",
  "Tarif récurrent": "Recurring rate",
  "Variable selon rail": "Varies by rail",
  "% + fee · selon rail": "% + fee · per rail",
  "Tarif spécifique frais scolaires": "Specific school rate",
  "Net payout · 0% retenue": "Net payout · 0% withholding",
  "0% retenue retour": "0% return withholding",
  "Traiteur absorbe les frais · Net payout": "Caterer absorbs fees · Net payout",
  "Fournisseur absorbe · Net payout": "Provider absorbs · Net payout",
  "Net payout par échéance": "Net payout per installment",
  "Net payout add-on": "Net payout · add-on",
  "Net payout cyclique": "Cyclic net payout",
  "Net payout traiteur": "Caterer net payout",
  "Net payout camp": "Camp net payout",
  "Net payout par versement": "Net payout per installment",
  "Net payout consolidé": "Consolidated net payout",
  "Montant ajouté au payout": "Amount added to payout",
  "Payé par réseau · pas EcoLunch": "Paid by network · not EcoLunch",
  "Settlement réseau": "Network settlement",
  "Payout interne": "Internal payout",

  /* ---- Node notes ---- */
  "ordered_by · paid_by · wallet_owner · child": "ordered_by · paid_by · wallet_owner · child",
  "total · prochaine · solde": "total · next · balance",
  "absences · crédits": "absences · credits",
  "2 paid_by · 1 service": "2 paid_by · 1 service",
  "solde · status": "balance · status",
  "inscription / repas": "registration / meals",
  "fee marqué payé · lié à élève": "fee marked paid · linked to student",
  "ajout au payout traiteur": "added to caterer payout",
  "Visa / Mastercard": "Visa / Mastercard",
  "Tokenisation": "Tokenization",
  "lié à tx originale": "linked to original tx",

  /* ---- Edge labels ---- */
  "Charge": "Charge",
  "Pay-in 100%": "Pay-in 100%",
  "Top-up": "Top-up",
  "Alimentation EcoWallet": "EcoWallet top-up",
  "Débit interne": "Internal debit",
  "Détient": "Holds",
  "Routage": "Routing",
  "Crédit EcoWallet": "EcoWallet credit",
  "Apple/Google/Samsung Pay": "Apple/Google/Samsung Pay",
  "Traiteur · sous-ledger": "Caterer · sub-ledger",
  "Garderie · sous-ledger": "Daycare · sub-ledger",
  "Camp / Club · sous-ledger": "Camp / Club · sub-ledger",
  "École · sous-ledger": "School · sub-ledger",
  "Entrée ajoutée au sous-ledger": "Entry added to sub-ledger",
  "Entrée payable": "Payable entry",
  "Allocate": "Allocate",
  "Enregistrement et allocation": "Recording and ledger allocation",
  "Règlement 100 %": "Full settlement (100%)",
  "Fonds non payables · en attente": "Funds non-payable · awaiting delivery",
  "Déclenche calcul du payable": "Triggers net payable computation",
  "Payable net après livraison": "Net payable after delivery",
  "Contexte de service": "Service context",
  "Ledger · fonds reçus": "Ledger · funds received",
  "Ledger · payable net calculé": "Ledger · net payable computed",
  "non payables": "non-payable",
  "déclenche le calcul du payable": "triggers net payable computation",
  "prix − ann./remb./CB/réserves/frais": "price − canc./refunds/CB/reserves/fees",
  "Acquit": "Settle",
  "Distribution": "Distribution",
  "Pay-in B2B": "B2B pay-in",
  "Charge institutionnelle": "Institutional charge",
  "Group order": "Group order",
  "Création commande": "Order creation",
  "Création facture": "Invoice creation",
  "Création échéancier": "Schedule creation",
  "Création": "Creation",
  "Record": "Record",
  "Record retour": "Record return",
  "Record subvention": "Record subsidy",
  "Choix": "Choice",
  "Order": "Order",
  "Per-day debit": "Per-day debit",
  "Per installment": "Per installment",
  "Pay-in cyclique": "Cyclic pay-in",
  "Pay-in n/N": "Pay-in n/N",
  "Pay-in global": "Global pay-in",
  "Pay-in 1": "Pay-in 1",
  "Débit": "Debit",
  "Débit cycle": "Cycle debit",
  "Inputs": "Inputs",
  "Montant final": "Final amount",
  "Part 1": "Part 1",
  "Part 2": "Part 2",
  "Split": "Split",
  "Mandat": "Mandate",
  "Mandate": "Mandate",
  "Service": "Service",
  "Attribution": "Assignment",
  "Activation": "Activation",
  "Membership": "Membership",
  "Tag inscription": "Tag registration",
  "Tag repas": "Tag meals",
  "Payout 1": "Payout 1",
  "Payout 2": "Payout 2",
  "Facture périodique": "Periodic invoice",
  "Facture complémentaire": "Add-on invoice",
  "Provision": "Provisioning",
  "Solde dispo": "Available balance",
  "Tap / online": "Tap / online",
  "Tap": "Tap",
  "Approve": "Approve",
  "Settlement": "Settlement",
  "Add to wallet": "Add to wallet",
  "Tokenize": "Tokenize",
  "Refund initiation": "Refund initiation",
  "Credit": "Credit",
  "Crédit": "Credit",
  "Solde restitué": "Balance restored",
  "Net payout (frais déduits)": "Net payout (fees deducted)",
  "EcoLunch · facture": "EcoLunch · invoice",
  "EcoLunch · facture mensuelle": "EcoLunch · monthly invoice",
  "Parent · commande": "Parent · order",
  "Parent · app EcoLunch": "Parent · EcoLunch app",
  "pas d'UI école": "no school UI",
  "Commande repas": "Meal order",
  "Cumul mensuel": "Monthly cumulation",
  "ne paie pas": "does not pay",
  "École · compte débité": "School · debited account",
  "PAD · débit auto": "PAD · auto-debit",
  "Émet facture": "Issues invoice",
  "Déclenche débit": "Triggers debit",
  "Compte débité": "Account debited",
  "Livraison confirmée": "Delivery confirmed",
  "Attend confirmation": "Awaits confirmation",
  "Net payout post-livraison": "Net payout post-delivery",
  "déclenche payout": "triggers payout",
  "Garderie · compte débité": "Daycare · debited account",
  "Camp · compte débité": "Camp · debited account",
  "Allocate per-child": "Allocate per-child",
};

/* Helper: translate a string FR → EN if requested */
export const flowT = (str, lang) => {
  if (!str) return str;
  if (lang !== "en") return str;
  return FR_EN_MAP[str] || str;
};

/* ---- Semantic colors per economic role ---- */
export const ROLE_COLORS = {
  payer:       "#a78bfa", // parent, daycare, camp, holder, sponsor (sources)
  hub:         "#bef264", // EcoLunch Hub, EcoWallet, Ledger (platform)
  rail:        "#22d3ee", // pay-in rail, card network
  ledger:      "#fbbf24", // ledger / record / bookkeeping
  order:       "#fb923c", // order, registration, invoice
  beneficiary: "#f472b6", // caterer, school, daycare, camp, club (payout recipients)
  external:    "#94a3b8", // tertiary / context (school context, sponsor when not payer)
};

/* Helper to keep code compact */
const N = (id, label, icon, role, x, y, step, opts = {}) => ({
  id, label, icon, color: ROLE_COLORS[role], role, x, y, step, ...opts,
});
const E = (from, to, label, opts = {}) => ({ from, to, label, ...opts });

/* ============================================================
   FLOW SCHEMAS
   ============================================================ */
export const FLOWS = {

  /* ---------- RESTAURATION ---------- */
  R1: {
    title: "R1 · Repas scolaires · le parent commande et paie pour son enfant",
    nodes: [
      N("parent",   "Parent",                          "Users",         "payer",       15, 12, 1),
      N("payin",    "Pay-in",                          "CreditCard",    "rail",        50, 12, 2, { feeIn: "% + fee · selon rail", note: "Carte · Apple/Google · Interac · Sezzle · PAD" }),
      N("hub",      "EcoLunch Hub",                    "fox",           "hub",         50, 44, 3, { hub: true }),
      N("ledger1",  "Ledger · fonds reçus",            "Database",      "ledger",      92, 44, 4, { note: "non payables" }),
      N("delivery", "Livraison confirmée",             "CheckCircle2",  "order",       92, 66, 5, { note: "déclenche le calcul du payable" }),
      N("ledger2",  "Ledger · payable net calculé",    "Database",      "ledger",      50, 76, 6, { note: "prix − ann./remb./CB/réserves/frais" }),
      N("caterer",  "Traiteur · sous-ledger",                        "ChefHat",       "beneficiary", 50, 93, 7),
      N("school",   "École · contexte",                "GraduationCap", "external",    15, 60, undefined, { dim: true }),
    ],
    edges: [
      E("parent",   "payin",    "Charge"),
      E("payin",    "hub",      "Règlement 100 %"),
      E("hub",      "ledger1",  "Enregistrement et allocation", { labelT: 0.55 }),
      E("ledger1",  "delivery", "Fonds non payables · en attente"),
      E("delivery", "ledger2",  "Déclenche calcul du payable"),
      E("ledger2",  "caterer",  "Payable net après livraison"),
      E("school",   "delivery", "Contexte de service"),
    ],
  },

  R2: {
    title: "R2 · Commande institutionnelle · commandeur et payeur distincts",
    nodes: [
      N("orderer",   "Gestionnaire interne", "ClipboardList","order",       18, 12, 1),
      N("account",   "Compte institutionnel","Building2",    "payer",       50, 12, 2),
      N("pad",       "PAD · débit auto",     "Banknote",     "rail",        82, 12, 3, { feeIn: "% + fee · selon rail" }),
      N("hub",       "EcoLunch Hub",         "fox",          "hub",         50, 50, 4, { hub: true }),
      N("ledger",    "Ledger · institutionnel","Database",   "ledger",      82, 50, 5, { note: "commandeur ≠ payeur" }),
      N("delivery",  "Livraison confirmée",  "CheckCircle2", "order",       82, 72, 6, { note: "déclenche payout" }),
      N("caterer",   "Traiteur · sous-ledger",             "ChefHat",      "beneficiary", 50, 90, 7),
    ],
    edges: [
      E("orderer",  "account",  "Crée commande"),
      E("orderer",  "pad",      "Déclenche débit", { arc: -12 }),
      E("account",  "pad",      "Compte débité"),
      E("pad",      "hub",      "Pay-in 100%"),
      E("hub",      "ledger",   "Record"),
      E("ledger",   "delivery", "Attend confirmation"),
      E("delivery", "caterer",  "Net payout post-livraison"),
    ],
  },

  R3: {
    title: "R3 · Camp ou club · choix entre lunch apporté et repas du traiteur",
    nodes: [
      N("parent",   "Parent",            "Users",         "payer",       50, 12, 1),
      N("dates",    "Sélection dates",   "CalendarCheck", "order",       18, 30, 2),
      N("payin",    "Pay-in",            "Banknote",      "rail",        82, 30, 3, { feeIn: "% + fee · selon rail", note: "Carte · Apple/Google · Interac · Sezzle · PAD" }),
      N("hub",      "EcoLunch Hub",      "fox",           "hub",         50, 50, 4, { hub: true }),
      N("ledger",   "Ledger · per-day",  "Database",      "ledger",      82, 65, 5),
      N("delivery", "Livraison confirmée","CheckCircle2", "order",       82, 80, 6, { note: "déclenche payout" }),
      N("caterer",  "Traiteur · sous-ledger",          "ChefHat",       "beneficiary", 50, 92, 7),
    ],
    edges: [
      E("parent",   "dates",    "Choix"),
      E("parent",   "payin",    "Charge", { labelT: 0.35 }),
      E("payin",    "hub",      "Pay-in", { labelT: 0.4 }),
      E("dates",    "hub",      "Order"),
      E("hub",      "ledger",   "Allocate"),
      E("ledger",   "delivery", "Attend confirmation"),
      E("delivery", "caterer",  "Net payout post-livraison"),
    ],
  },

  R4: {
    title: "R4 · Camp ou club · repas fournis à tous · modèle institutionnel",
    nodes: [
      N("camp",     "Camp",                  "Tent",         "payer",       50, 12, 1),
      N("payin",    "Chargement institutionnel","Banknote",  "rail",        18, 30, 2, { feeIn: "% + fee · selon rail" }),
      N("hub",      "EcoLunch Hub",          "fox",          "hub",         50, 50, 3, { hub: true }),
      N("ledger",   "Ledger · per-child",    "Database",     "ledger",      82, 50, 4),
      N("delivery", "Livraison confirmée",   "CheckCircle2", "order",       82, 72, 5, { note: "déclenche payout" }),
      N("caterer",  "Traiteur · sous-ledger",              "ChefHat",      "beneficiary", 50, 90, 6),
    ],
    edges: [
      E("camp",     "payin",    "Pay-in global"),
      E("payin",    "hub",      "Top-up", { labelT: 0.4 }),
      E("hub",      "ledger",   "Allocate"),
      E("ledger",   "delivery", "Attend confirmation"),
      E("delivery", "caterer",  "Net payout post-livraison"),
    ],
  },

  R5: {
    title: "R5 · Repas subventionnés · le parent commande · l'école paie mensuellement",
    nodes: [
      N("parent",        "Parent · commande",    "Users",        "payer",       18, 12, 1, { note: "ne paie pas" }),
      N("monthly_inv",   "EcoLunch · facture mensuelle","ClipboardList","order", 50, 12, 2),
      N("school",        "École · compte débité","GraduationCap","payer",       82, 12, 3),
      N("pad",           "PAD · débit auto",     "Banknote",     "rail",        82, 32, 4, { feeIn: "% + fee · selon rail" }),
      N("hub",           "EcoLunch Hub",         "fox",          "hub",         50, 50, 5, { hub: true }),
      N("ledger",        "Ledger · subvention",  "Database",     "ledger",      82, 50, 6, { note: "ajout au payout traiteur" }),
      N("delivery",      "Livraison confirmée",  "CheckCircle2", "order",       82, 72, 7, { note: "déclenche payout" }),
      N("caterer",       "Traiteur · payable",   "ChefHat",      "beneficiary", 50, 92, 8),
    ],
    edges: [
      E("parent",      "monthly_inv","Commande repas"),
      E("monthly_inv", "school",     "Cumul mensuel"),
      E("monthly_inv", "pad",        "Déclenche débit", { arc: -12 }),
      E("school",      "pad",        "Compte débité"),
      E("pad",         "hub",        "Pay-in 100%"),
      E("hub",         "ledger",     "Record subvention", { labelT: 0.45 }),
      E("ledger",      "delivery",   "Attend confirmation"),
      E("delivery",    "caterer",    "Net payout post-livraison"),
    ],
  },

  /* ---------- GARDE ---------- */
  G1: {
    title: "G1 · Garderie · inscription et frais ponctuels",
    nodes: [
      N("daycare", "Garderie · facture",  "Building2",  "order",       18, 12, 1),
      N("parent",  "Parent",              "Users",      "payer",       82, 12, 2),
      N("payin",   "Pay-in",              "CreditCard", "rail",        82, 28, 3, { feeIn: "% + fee · selon rail", note: "Carte · Apple/Google · Interac · Sezzle · PAD" }),
      N("hub",     "EcoLunch Hub",        "fox",        "hub",         50, 50, 4, { hub: true }),
      N("ledger",  "Ledger",              "Database",   "ledger",      82, 70, 6),
      N("daypay",  "Garderie · sous-ledger",  "Building2",  "beneficiary", 50, 88, 7),
    ],
    edges: [
      E("daycare", "hub",     "Création", { arc: -10 }),
      E("parent",  "payin",   "Charge", { labelT: 0.35 }),
      E("payin",   "hub",     "Pay-in", { labelT: 0.4 }),
      E("hub",      "ledger",   "Allocate"),
      E("ledger",  "daypay",  "Entrée payable"),
    ],
  },

  G2: {
    title: "G2 · Garderie · frais de garde récurrents",
    nodes: [
      N("daycare", "Garderie · calendrier","CalendarCheck","external", 12, 30, 1, { dim: true }),
      N("parent",  "Parent",              "Users",         "payer",    50, 12, 2),
      N("pad",     "Auto-load récurrent", "Repeat",        "rail",     82, 12, 3, { feeIn: "% + fee · selon rail", note: "PAD · Carte · Interac récurrent" }),
      N("hub",     "EcoLunch Hub",        "fox",           "hub",      50, 50, 4, { hub: true }),
      N("ledger",  "Ledger · périodique", "Database",      "ledger",   82, 50, 6),
      N("daypay",  "Garderie · sous-ledger",  "Building2",     "beneficiary", 50, 88, 7),
    ],
    edges: [
      E("daycare", "hub",     "Facture périodique", { arc: -10 }),
      E("parent",  "pad",     "Mandat"),
      E("pad",     "hub",     "Pay-in cyclique"),
      E("hub",      "ledger",   "Allocate"),
      E("ledger",  "daypay",  "Entrée payable"),
    ],
  },

  G3: {
    title: "G3 · Garderie · frais ajustés selon les présences et les absences",
    nodes: [
      N("attendance","Présences validées","CheckCircle2","external",   8, 30, 1, { dim: true }),
      N("daycare",   "Moteur de facturation","Cog",     "external",      30, 30, 2, { dim: true }),
      N("parent",    "Parent",            "Users",       "payer",       60, 12, 3),
      N("payin",     "Pay-in",            "CreditCard",  "rail",        88, 12, 4, { feeIn: "% + fee · selon rail", note: "Carte · Apple/Google · Interac · Sezzle · PAD" }),
      N("hub",       "EcoLunch Hub",      "fox",         "hub",         50, 55, 5, { hub: true }),
      N("ledger",    "Ledger · ajusté",   "Database",    "ledger",      82, 55, 7, { note: "absences · crédits" }),
      N("daypay",    "Garderie · sous-ledger","Building2",   "beneficiary", 50, 90, 8),
    ],
    edges: [
      E("attendance","daycare", "Inputs"),
      E("daycare",   "hub",     "Montant final"),
      E("parent",    "payin",   "Charge"),
      E("payin",     "hub",     "Pay-in"),
      E("hub",      "ledger",   "Allocate"),
      E("ledger",    "daypay",  "Entrée payable"),
    ],
  },

  G4: {
    title: "G4 · Garderie · frais répartis entre plusieurs payeurs",
    nodes: [
      N("daycare",  "Garderie",           "Building2",  "external",    8, 30, 1, { dim: true }),
      N("parent",   "Parent principal",   "Users",      "payer",       30, 12, 2),
      N("coparent", "Coparent / tier",    "Users",      "payer",       70, 12, 3),
      N("payin1",   "Pay-in 50%",         "CreditCard", "rail",        30, 32, 4, { feeIn: "% + fee · selon rail", note: "Carte · Apple/Google · Interac · Sezzle · PAD", compact: true }),
      N("payin2",   "Pay-in 50%",         "CreditCard", "rail",        70, 32, 5, { feeIn: "% + fee · selon rail", note: "Carte · Apple/Google · Interac · Sezzle · PAD", compact: true }),
      N("hub",      "EcoLunch Hub",       "fox",        "hub",         50, 55, 6, { hub: true }),
      N("ledger",   "Ledger · split",     "Database",   "ledger",      82, 72, 7, { note: "2 paid_by · 1 service" }),
      N("daypay",   "Garderie · sous-ledger", "Building2",  "beneficiary", 50, 92, 8),
    ],
    edges: [
      E("daycare", "hub",     "Service", { arc: -10 }),
      E("parent",  "payin1",  "Part 1"),
      E("coparent","payin2",  "Part 2"),
      E("payin1",  "hub",     "Pay-in", { labelT: 0.4 }),
      E("payin2",  "hub",     "Pay-in", { labelT: 0.4 }),
      E("hub",     "ledger",  "Split"),
      E("ledger",  "daypay",  "Entrée payable"),
    ],
  },

  /* ---------- SPORT & LOISIR ---------- */
  L1: {
    title: "L1 · Camp ou club · inscription payée en totalité",
    nodes: [
      N("club",    "Camp / Club · activité","Tent",     "order",       18, 12, 1),
      N("parent",  "Parent",                "Users",    "payer",       82, 12, 2),
      N("payin",   "Pay-in",                "CreditCard","rail",       82, 35, 3, { feeIn: "% + fee · selon rail", note: "Carte · Apple/Google · Interac · Sezzle · PAD" }),
      N("hub",     "EcoLunch Hub",          "fox",      "hub",         50, 50, 4, { hub: true }),
      N("ledger",  "Ledger",                "Database", "ledger",      82, 70, 6),
      N("clubp",   "Camp / Club · sous-ledger", "Tent",     "beneficiary", 50, 88, 7),
    ],
    edges: [
      E("club",   "hub",    "Publication"),
      E("parent", "payin",  "Charge"),
      E("payin",  "hub",    "Pay-in", { labelT: 0.4 }),
      E("hub",      "ledger",   "Allocate"),
      E("ledger", "clubp",  "Entrée payable"),
    ],
  },

  L2: {
    title: "L2 · Camp ou club · inscription payée en versements",
    nodes: [
      N("club",     "Inscription confirmée","Tent",     "order",       18, 12, 1),
      N("parent",   "Parent",               "Users",    "payer",       82, 12, 2),
      N("deposit",  "Dépôt initial",        "CreditCard","rail",       50, 18, 3, { feeIn: "% + fee · selon rail", note: "Carte · Apple/Google · Interac · Sezzle · PAD" }),
      N("autoload", "Auto-loads programmés","Repeat",   "rail",        82, 30, 4, { feeIn: "% + fee · selon rail", note: "PAD · Carte · Interac récurrent" }),
      N("hub",      "EcoLunch Hub",         "fox",      "hub",         50, 55, 5, { hub: true }),
      N("ledger",   "Ledger · échéancier",  "Database", "ledger",      18, 55, 6, { note: "solde · status" }),
      N("clubp",    "Camp / Club · sous-ledger","Tent",     "beneficiary", 50, 88, 7),
    ],
    edges: [
      E("club",    "hub",      "Activation"),
      E("parent",  "deposit",  "Charge"),
      E("parent",  "autoload", "Mandat"),
      E("deposit", "hub",      "Pay-in 1", { labelT: 0.4 }),
      E("autoload","hub",      "Pay-in n/N", { labelT: 0.4 }),
      E("hub",     "ledger",   "Record"),
      E("ledger",  "clubp",    "Net payout échelonné"),
    ],
  },

  L3: {
    title: "L3 · Club · abonnement et frais récurrents",
    nodes: [
      N("club",     "Club · adhésion",     "Tent",     "order",       18, 12, 1),
      N("parent",   "Parent",              "Users",    "payer",       82, 12, 2),
      N("autoload", "Auto-load périodique","Repeat",   "rail",        82, 35, 3, { feeIn: "% + fee · selon rail", note: "PAD · Carte · Interac récurrent" }),
      N("hub",      "EcoLunch Hub",        "fox",      "hub",         50, 50, 4, { hub: true }),
      N("ledger",   "Ledger · cycle",      "Database", "ledger",      82, 70, 6),
      N("clubp",    "Club · payable",      "Tent",     "beneficiary", 50, 88, 7),
    ],
    edges: [
      E("club",     "hub",     "Membership"),
      E("parent",   "autoload","Mandat"),
      E("autoload", "hub",     "Pay-in cyclique", { labelT: 0.4 }),
      E("hub",      "ledger",   "Allocate"),
      E("ledger",   "clubp",   "Entrée payable"),
    ],
  },

  L4: {
    title: "L4 · Camp ou club · options et frais additionnels",
    nodes: [
      N("club",    "Camp · option ajoutée","Plus",     "order",       18, 12, 1),
      N("parent",  "Parent",               "Users",    "payer",       82, 12, 2),
      N("payin",   "Pay-in",               "CreditCard","rail",       82, 35, 3, { feeIn: "% + fee · selon rail", note: "Carte · Apple/Google · Interac · Sezzle · PAD" }),
      N("hub",     "EcoLunch Hub",         "fox",      "hub",         50, 50, 4, { hub: true }),
      N("ledger",  "Ledger · add-on",      "Database", "ledger",      82, 70, 6),
      N("clubp",   "Camp / Club · sous-ledger","Tent",     "beneficiary", 50, 88, 7),
    ],
    edges: [
      E("club",   "hub",    "Facture complémentaire"),
      E("parent", "payin",  "Charge"),
      E("payin",  "hub",    "Pay-in", { labelT: 0.4 }),
      E("hub",      "ledger",   "Allocate"),
      E("ledger", "clubp",  "Entrée payable"),
    ],
  },

  L5: {
    title: "L5 · Camp · inscription et repas du traiteur dans un même panier",
    nodes: [
      N("parent",  "Parent",               "Users",      "payer",       50, 12, 1),
      N("club",    "Camp · inscription",   "Tent",       "order",       18, 12, 2),
      N("camp_meal","Camp · repas inclus", "ChefHat",    "order",       82, 12, 3),
      N("payin",   "Pay-in unique",        "CreditCard", "rail",        50, 32, 4, { feeIn: "% + fee · selon rail", note: "Carte · Apple/Google · Interac · Sezzle · PAD" }),
      N("hub",     "EcoLunch Hub",         "fox",        "hub",         50, 52, 5, { hub: true }),
      N("ledger",  "Ledger · split rule",  "Database",   "ledger",      82, 52, 6, { note: "inscription / repas" }),
      N("clubp",   "Camp · payable",       "Tent",       "beneficiary", 28, 88, 8),
      N("caterer", "Traiteur · payable",   "ChefHat",    "beneficiary", 72, 88, 9),
    ],
    edges: [
      E("club",     "hub",     "Tag inscription"),
      E("camp_meal","hub",     "Tag repas"),
      E("parent",   "payin",   "Charge"),
      E("payin",    "hub",     "Pay-in unique", { labelT: 0.45 }),
      E("hub",      "ledger",   "Allocate"),
      E("ledger",   "clubp",   "Payout 1"),
      E("ledger",   "caterer", "Payout 2"),
    ],
  },

  /* ---------- SERVICES FINANCIERS (Phase 4) ---------- */
  F1: {
    title: "F1 · EcoWallet fermé · paiements dans l'écosystème EcoLunch",
    nodes: [
      N("holder",  "Titulaire",          "Users",      "payer",       15, 12, 1),
      N("source",  "Source de fonds",    "Banknote",   "rail",        50, 22, 2, { feeIn: "% + fee · selon rail" }),
      N("hub",     "EcoLunch Hub",       "fox",        "hub",         50, 50, 3, { hub: true }),
      N("ledger",  "Ledger",             "Database",   "ledger",      82, 50, 4),
      N("purchase","Achat interne",      "ShoppingCart","order",      50, 75, 5),
      N("internal","Bénéficiaire interne","Layers",    "beneficiary", 50, 92, 6),
    ],
    edges: [
      E("holder",  "source",  "Charge"),
      E("source",  "hub",     "Alimentation EcoWallet"),
      E("hub",     "ledger",  "Record"),
      E("hub",     "purchase","Débit interne"),
      E("ledger",  "internal","Entrée payable"),
    ],
  },

  F2: {
    title: "F2 · Carte virtuelle prépayée Visa ou Mastercard",
    nodes: [
      N("holder",   "Titulaire",        "Users",      "payer",       15, 12, 1),
      N("card",     "Carte virtuelle",  "CreditCard", "rail",        50, 12, 2, { note: "Visa / Mastercard" }),
      N("network",  "Réseau cartes",    "Network",    "rail",        85, 22, 3),
      N("hub",      "EcoLunch Hub",     "fox",        "hub",         50, 50, 4, { hub: true }),
      N("authzn",   "Autorisation",     "ShieldCheck","rail",        85, 50, 5, { feeIn: "% + fee · selon rail" }),
      N("ledger",   "Ledger · external","Database",   "ledger",      15, 50, 6),
      N("merchant", "Commerçant externe","Building2", "beneficiary", 50, 88, 7),
    ],
    edges: [
      E("holder",  "card",    "Détient"),
      E("card",    "network", "Tap / online"),
      E("network", "authzn",  "Auth"),
      E("authzn",  "hub",     "Approve"),
      E("hub",     "ledger",  "Record (référence)"),
      E("hub",     "merchant","Settlement"),
    ],
  },

  F3: {
    title: "F3 · Carte EcoCard dans Apple Wallet, Google Wallet ou Samsung Wallet",
    nodes: [
      N("holder",  "Titulaire",         "Users",      "payer",       15, 12, 1),
      N("card",    "Carte virtuelle",   "CreditCard", "rail",        50, 12, 2),
      N("token",   "Apple/Google/Samsung Pay","Smartphone", "rail",  85, 22, 3, { note: "Tokenisation" }),
      N("hub",     "EcoLunch Hub",      "fox",        "hub",         50, 50, 4, { hub: true }),
      N("authzn",  "Autorisation",      "ShieldCheck","rail",        85, 50, 5, { feeIn: "% + fee · selon rail" }),
      N("ledger",  "Ledger",            "Database",   "ledger",      15, 50, 6),
      N("merchant","Commerçant externe","Building2",  "beneficiary", 50, 88, 7),
    ],
    edges: [
      E("holder", "card",    "Détient"),
      E("card",   "token",   "Tokenize"),
      E("token",  "authzn",  "Tap"),
      E("authzn", "hub",     "Approve"),
      E("hub",    "ledger",  "Record"),
      E("hub",    "merchant","Settlement"),
    ],
  },

  F4: {
    title: "F4 · Remboursement d'un achat effectué à l'extérieur d'EcoLunch",
    nodes: [
      N("merchant","Commerçant externe","Building2", "external",    50, 10, 1),
      N("network", "Réseau cartes",     "Network",   "rail",        50, 28, 2, { feeIn: "% + fee · selon rail" }),
      N("card",    "Carte virtuelle",   "CreditCard","rail",        18, 50, 3),
      N("hub",     "EcoLunch Hub",      "fox",       "hub",         50, 50, 4, { hub: true }),
      N("ledger",  "Ledger · chain ID", "Database",  "ledger",      82, 50, 5, { note: "lié à tx originale" }),
      N("holder",  "Titulaire · solde", "Users",     "beneficiary", 50, 90, 6),
    ],
    edges: [
      E("merchant","network", "Refund initiation"),
      E("network", "card",    "Credit"),
      E("card",    "hub",     "Routage"),
      E("hub",     "ledger",  "Record retour"),
      E("hub",     "holder",  "Crédit EcoWallet"),
    ],
  },

  /* ---------- FRAIS SCOLAIRES (parents) ---------- */
  S1: {
    title: "S1 · Frais scolaires · paiement unique par le parent",
    nodes: [
      N("school",  "École / CSS · facturier","GraduationCap","external", 18, 12, 1, { dim: true, note: "pas d'UI école" }),
      N("parent",  "Parent · app EcoLunch","Users",        "payer",    82, 12, 2),
      N("payin",   "Pay-in",                "CreditCard",  "rail",     82, 25, 3, { feeIn: "% + fee · selon rail", note: "Carte · Apple/Google · Interac · Sezzle · PAD" }),
      N("hub",     "EcoLunch Hub",          "fox",         "hub",      50, 50, 4, { hub: true }),
      N("ledger",  "Ledger",                "Database",    "ledger",   82, 60, 5),
      N("schoolp", "École · sous-ledger",       "GraduationCap","beneficiary", 50, 88, 6),
    ],
    edges: [
      E("school",  "hub",     "Création facture", { arc: -10 }),
      E("parent",  "payin",   "Charge", { labelT: 0.35 }),
      E("payin",   "hub",     "Pay-in 100%", { labelT: 0.4 }),
      E("hub",     "ledger",  "Allocate", { labelT: 0.6 }),
      E("ledger",  "schoolp", "Entrée payable"),
    ],
  },

  S2: {
    title: "S2 · Frais scolaires · versements ou auto-load",
    nodes: [
      N("school",   "École · échéancier",   "CalendarCheck","external", 18, 30, 1, { dim: true }),
      N("parent",   "Parent · app EcoLunch","Users",        "payer",    50, 12, 2),
      N("autoload", "Auto-load programmé",  "Repeat",       "rail",     82, 12, 3, { feeIn: "% + fee · selon rail", note: "PAD · Carte · Interac récurrent" }),
      N("hub",      "EcoLunch Hub",         "fox",          "hub",      50, 50, 4, { hub: true }),
      N("ledger",   "Ledger · échéances",   "Database",     "ledger",   82, 50, 5, { note: "total · prochaine · solde" }),
      N("schoolp",  "École · sous-ledger",      "GraduationCap","beneficiary", 50, 88, 6),
    ],
    edges: [
      E("school",  "hub",      "Création échéancier", { arc: -10 }),
      E("parent",  "autoload", "Mandat"),
      E("autoload","hub",      "Pay-in n/N"),
      E("hub",     "ledger",   "Allocate"),
      E("ledger",  "schoolp",  "Entrée payable"),
    ],
  },
};

/* English translation of edge labels and node labels for each schema */
export const FLOWS_EN_LABELS = {
  R1: { title: "R1 · School meals · the parent orders and pays for their child" },
  R2: { title: "R2 · Institutional order · orderer and payer distinct" },
  R3: { title: "R3 · Camp or club · choice between brought lunch and caterer meal" },
  R4: { title: "R4 · Camp or club · meals provided to everyone · institutional model" },
  R5: { title: "R5 · Subsidized meals · the parent orders · the school is billed monthly" },
  G1: { title: "G1 · Daycare · registration and one-off fees" },
  G2: { title: "G2 · Daycare · recurring childcare fees" },
  G3: { title: "G3 · Daycare · fees adjusted for attendance and absences" },
  G4: { title: "G4 · Daycare · fees split across several payers" },
  L1: { title: "L1 · Camp or club · registration paid in full" },
  L2: { title: "L2 · Camp or club · registration paid in installments" },
  L3: { title: "L3 · Club · subscription and recurring fees" },
  L4: { title: "L4 · Camp or club · add-ons and additional fees" },
  L5: { title: "L5 · Camp · registration and caterer meals in a single cart" },
  F1: { title: "F1 · Closed EcoWallet · payments inside the EcoLunch ecosystem" },
  F2: { title: "F2 · Prepaid virtual Visa or Mastercard card" },
  F3: { title: "F3 · EcoCard in Apple Wallet, Google Wallet or Samsung Wallet" },
  F4: { title: "F4 · Refund of a purchase made outside EcoLunch" },
  S1: { title: "S1 · School fees · single payment by the parent" },
  S2: { title: "S2 · School fees · payment in scheduled installments" },
};
